import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Hls from "hls.js";
import Features from './Features';
import Navigation from './Navigation';
import { trackEvent } from '../utils/analytics';
import { WALKTHROUGHS } from './WalkthroughVideo';

/**
 * Props:
 * - src: URL to your HLS master playlist (e.g., https://cdn.example.com/launch/master.m3u8)
 * - poster: optional poster image
 * - fallbackMp4: optional progressive MP4 fallback (e.g., your 1080p web-optimized mp4)
 */
export default function DemoVideo({
  src = "/hls/demo/master.m3u8",
  poster = "/hls/demo/solidly-finance-poster.jpg",
  fallbackMp4 = "/hls/demo/solidly-launch-720p-fallback.mp4"
}) {
  const videoRef = useRef(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);

    // Track that user viewed the demo video page
    trackEvent('demo_video_page_view', {
      event_category: 'engagement',
      event_label: 'demo_video_page',
      value: 1
    });
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    // If the browser supports HLS natively (Safari, iOS), just set src
    const canPlayNativeHls =
      video.canPlayType("application/vnd.apple.mpegurl") ||
      video.canPlayType("application/x-mpegURL");

    function onVideoError() {
      const mediaErr = video.error;
      if (mediaErr) {
        setError(`Playback error (code ${mediaErr.code}).`);
      } else {
        setError("Playback error.");
      }
    }

    if (canPlayNativeHls) {
      video.src = src;
      video.addEventListener("error", onVideoError);
      return () => video.removeEventListener("error", onVideoError);
    }

    // Otherwise use hls.js for MSE playback (Chrome/Firefox/Edge)
    if (Hls.isSupported()) {
      const hls = new Hls({
        abrEwmaDefaultEstimate: 8_000_000,     // ~8 Mbps initial guess
        abrMaxWithRealBitrate: true,           // use measured bitrate per level
        capLevelToPlayerSize: false,           // don't cap to CSS size
        startLevel: 2                          // 0=360p, 1=720p, 2=1080p  ← force 1080p start
      });
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src);
      });
      hls.on(Hls.Events.ERROR, (_, data) => {
        console.warn("hls.js error", data);
        if (data.fatal) {
          setError("Playback error. Try reloading or a different network.");
          hls.destroy();
        }
      });

      video.addEventListener("error", onVideoError);

      return () => {
        video.removeEventListener("error", onVideoError);
        hls.destroy();
      };
    }

    // Last resort: fall back to MP4 if provided
    if (fallbackMp4) {
      video.src = fallbackMp4;
    } else {
      setError("Your browser doesn't support HLS playback.");
    }

    video.addEventListener("error", onVideoError);
    return () => video.removeEventListener("error", onVideoError);
  }, [src, fallbackMp4]);

  const handleBackToHome = () => {
    trackEvent('demo_video_back_to_home', {
      event_category: 'navigation',
      event_label: 'demo_video_page',
      value: 1
    });
    navigate('/');
  };

  const handleWalkthroughClick = (walkthroughId) => {
    trackEvent('walkthrough_poster_click', {
      event_category: 'engagement',
      event_label: `walkthrough_${walkthroughId}`,
      value: 1
    });
  };

  // Create VideoObject structured data for SEO
  const videoSchema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: 'See Solidly Finance in Action',
    description: 'Watch how Solidly Finance helps you chart your path to financial freedom with comprehensive personal finance planning tools.',
    thumbnailUrl: `https://finance.solidly.ai${poster}`,
    uploadDate: '2025-10-29',
    contentUrl: `https://finance.solidly.ai${fallbackMp4}`,
    embedUrl: 'https://finance.solidly.ai/demo',
    duration: 'PT3M', // ISO 8601 duration format (3 minutes)
  };

  return (
    <div className="demo-video-page">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(videoSchema)}
        </script>
      </Helmet>

      <Navigation 
        showDemoButton={false}
        secondaryButtonText="Back to Home"
        onSecondaryClick={() => navigate('/')}
        trackingLocation="demo_video_page"
      />

      <div className="demo-video-container">
        <div className="demo-video-header">
          <h1>See Solidly Finance in Action</h1>
          <p className="demo-video-subtitle">
            Watch how Solidly Finance helps you chart your path to financial freedom
          </p>
        </div>

        <div className="video-wrapper">
          <div className="demo-video">
            <video
              ref={videoRef}
              className="demo-video"
              playsInline
              controls
              preload="metadata"
              poster={poster}
              style={{ width: "100%", height: "auto", borderRadius: 12 }}
            />
            {error && (
              <p style={{ color: "tomato", marginTop: 8 }}>
                {error}
              </p>
            )}
          </div>
        </div>

        {/* Walkthrough Videos Section */}
        <div className="walkthrough-posters-section">
          <h2 className="walkthrough-section-title">Video Walkthroughs</h2>
          <p className="walkthrough-section-subtitle">
            Step-by-step guides to help you master Solidly Finance
          </p>
          <div className="walkthrough-posters-grid">
            {Object.entries(WALKTHROUGHS).map(([id, walkthrough]) => (
              <Link 
                key={id}
                to={`/walkthrough/${id}`} 
                className="walkthrough-poster-link"
                onClick={() => handleWalkthroughClick(id)}
              >
                <img 
                  src={walkthrough.poster}
                  alt={`Walkthrough ${id}: ${walkthrough.title}`}
                  className="walkthrough-poster-image"
                />
                <div className="walkthrough-poster-overlay">
                  <h3 className="walkthrough-poster-title">{walkthrough.title}</h3>
                  <p className="walkthrough-poster-description">
                    {walkthrough.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <Features />
    </div>
  );
}
